/**
 * True when an Unlayer design has its top-level `body`, the one key the editor can't load without.
 *
 * The same rule the API holds (`UnlayerDesign.HasBody`): an editor that never finished loading exports `{}`,
 * and saving that wiped a real template's design (ORB-960). The API refuses it too; checking here means the user
 * is told before a save is attempted.
 */
export function hasEmailBody(design: unknown): boolean {
    if (design === null || typeof design !== 'object' || Array.isArray(design)) return false
    return (design as { body?: unknown }).body != null
}

// Not `hideable`: a hidden footer still compiles its address and link into the HTML, so the check on save would pass
// an email whose recipients see neither (PR #273 review).
const LOCK_FLAGS = ['draggable', 'duplicatable', 'deletable'] as const

type DesignNode = { values?: Record<string, unknown> }
type DesignRow = DesignNode & { columns?: { contents?: DesignNode[] }[] }

/**
 * The design with no row or block locked in place. From ORB-827 until ORB-927 the event details and footer were
 * locked, so a template or copy saved then still carries the lock; clearing it as the design loads frees them with no
 * data conversion. The flags only steer the editor: the HTML it compiles, and so the email, is the same.
 */
export function unlockDesign<T>(design: T): T {
    if (!hasEmailBody(design)) return design
    const copy = structuredClone(design) as { body: { rows?: DesignRow[] } }
    const unlock = (node: DesignNode) => {
        const values = node.values
        if (!values) return
        for (const flag of LOCK_FLAGS) {
            if (values[flag] === false) values[flag] = true
        }
    }
    for (const row of copy.body.rows ?? []) {
        unlock(row)
        for (const column of row.columns ?? []) {
            for (const content of column.contents ?? []) unlock(content)
        }
    }
    return copy as T
}
